import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { dayDetail, type DayDetail } from '@/db/queries'

interface Props {
  day: string | null
  onClose?: () => void
}

export default function DayDetailPanel({ day, onClose }: Props) {
  const [detail, setDetail] = useState<DayDetail | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!day) {
      setDetail(null)
      return
    }
    setLoading(true)
    dayDetail(day).then((d) => {
      setDetail(d)
      setLoading(false)
    })
  }, [day])

  if (!day) {
    return (
      <div className="rounded-xl border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-500">
        Click any day in the calendar to see the full picture — recovery, strain,
        sleep, and workouts.
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-stone-200 bg-white overflow-hidden shadow-sm">
      <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
        <div>
          <div className="text-xs uppercase tracking-wider text-stone-500">Day detail</div>
          <div className="text-2xl font-semibold mt-0.5">{prettyDate(day)}</div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-sm text-stone-500 hover:text-stone-900 px-2 py-1"
            aria-label="Close"
          >
            ✕
          </button>
        )}
      </div>

      {loading || !detail ? (
        <div className="p-8 text-sm text-stone-500">Loading…</div>
      ) : (
        <div className="p-5 space-y-6">
          <TopStats detail={detail} />
          <SleepSection detail={detail} />
          <WorkoutsSection detail={detail} />
        </div>
      )}
    </div>
  )
}

function TopStats({ detail }: { detail: DayDetail }) {
  const recovery = detail.recovery?.score?.recovery_score
  const strain = detail.cycle?.score?.strain
  const hrv = detail.recovery?.score?.hrv_rmssd_milli
  const rhr = detail.recovery?.score?.resting_heart_rate
  const sleepPerf = detail.sleep?.score?.sleep_performance_percentage
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <Stat
        label="Recovery"
        value={recovery != null ? `${Math.round(recovery)}%` : '—'}
        tone={recoveryTone(recovery)}
      />
      <Stat
        label="Strain"
        value={strain != null ? strain.toFixed(1) : '—'}
        tone="strain"
      />
      <Stat label="HRV" value={hrv != null ? `${Math.round(hrv)} ms` : '—'} />
      <Stat label="Resting HR" value={rhr != null ? `${Math.round(rhr)} bpm` : '—'} />
      <Stat
        label="Sleep perf"
        value={sleepPerf != null ? `${Math.round(sleepPerf)}%` : '—'}
        tone="sleep"
      />
    </div>
  )
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'recovery-high' | 'recovery-mid' | 'recovery-low' | 'strain' | 'sleep'
}) {
  return (
    <div className="rounded-lg border border-stone-200 bg-stone-50 p-3">
      <div className="text-[11px] uppercase tracking-wider text-stone-500">{label}</div>
      <div
        className={clsx(
          'text-xl font-semibold tabular-nums mt-1',
          tone === 'recovery-high' && 'text-whoop-recovery-high',
          tone === 'recovery-mid' && 'text-whoop-recovery-mid',
          tone === 'recovery-low' && 'text-whoop-recovery-low',
          tone === 'strain' && 'text-whoop-strain',
          tone === 'sleep' && 'text-whoop-sleep',
          !tone && 'text-stone-900',
        )}
      >
        {value}
      </div>
    </div>
  )
}

function recoveryTone(score?: number) {
  if (score == null) return undefined
  if (score >= 67) return 'recovery-high' as const
  if (score >= 34) return 'recovery-mid' as const
  return 'recovery-low' as const
}

function SleepSection({ detail }: { detail: DayDetail }) {
  const s = detail.sleep
  if (!s || !s.score) {
    return (
      <Section title="Sleep">
        <div className="text-sm text-stone-500">No sleep recorded.</div>
      </Section>
    )
  }
  const stages = s.score.stage_summary
  const inBed = stages.total_in_bed_time_milli
  const awake = stages.total_awake_time_milli
  const noData = stages.total_no_data_time_milli
  const asleep = inBed - awake - noData
  return (
    <Section title="Sleep">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <Field label="Time in bed" value={formatHm(inBed)} />
        <Field label="Asleep" value={formatHm(asleep)} />
        <Field
          label="Performance"
          value={
            s.score.sleep_performance_percentage != null
              ? `${Math.round(s.score.sleep_performance_percentage)}%`
              : '—'
          }
        />
        <Field
          label="Consistency"
          value={
            s.score.sleep_consistency_percentage != null
              ? `${Math.round(s.score.sleep_consistency_percentage)}%`
              : '—'
          }
        />
      </div>
      <div className="mt-3">
        <SleepStagesBar
          rem={stages.total_rem_sleep_time_milli}
          sws={stages.total_slow_wave_sleep_time_milli}
          light={stages.total_light_sleep_time_milli}
          awake={awake}
        />
      </div>
      {detail.naps.length > 0 && (
        <div className="mt-3 text-xs text-stone-500">
          + {detail.naps.length} nap{detail.naps.length === 1 ? '' : 's'}
        </div>
      )}
    </Section>
  )
}

function SleepStagesBar({
  rem,
  sws,
  light,
  awake,
}: {
  rem: number
  sws: number
  light: number
  awake: number
}) {
  const total = rem + sws + light + awake
  if (!total) return null
  const seg = (ms: number) => `${(ms / total) * 100}%`
  return (
    <div>
      <div className="flex w-full h-3 rounded-md overflow-hidden bg-stone-100">
        <div className="bg-indigo-500" style={{ width: seg(sws) }} title={`Deep ${formatHm(sws)}`} />
        <div className="bg-purple-500" style={{ width: seg(rem) }} title={`REM ${formatHm(rem)}`} />
        <div className="bg-purple-300" style={{ width: seg(light) }} title={`Light ${formatHm(light)}`} />
        <div className="bg-stone-300" style={{ width: seg(awake) }} title={`Awake ${formatHm(awake)}`} />
      </div>
      <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-stone-500">
        <Legend color="bg-indigo-500" label={`Deep ${formatHm(sws)}`} />
        <Legend color="bg-purple-500" label={`REM ${formatHm(rem)}`} />
        <Legend color="bg-purple-300" label={`Light ${formatHm(light)}`} />
        <Legend color="bg-stone-300" label={`Awake ${formatHm(awake)}`} />
      </div>
    </div>
  )
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={clsx('inline-block w-2 h-2 rounded-sm', color)} />
      {label}
    </span>
  )
}

function WorkoutsSection({ detail }: { detail: DayDetail }) {
  if (!detail.workouts.length) {
    return (
      <Section title="Workouts">
        <div className="text-sm text-stone-500">No workouts recorded.</div>
      </Section>
    )
  }
  return (
    <Section title={`Workouts (${detail.workouts.length})`}>
      <div className="space-y-2">
        {detail.workouts.map((w) => {
          const dur = w.start && w.end ? +new Date(w.end) - +new Date(w.start) : 0
          const sport = capitalize(w.sport_name ?? 'Activity')
          return (
            <div
              key={w.id}
              className="rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{sport}</div>
                <div className="text-xs text-stone-500">
                  {timeOfDay(w.start)} · {formatHm(dur)}
                  {w.score?.distance_meter
                    ? ` · ${(w.score.distance_meter / 1000).toFixed(2)} km`
                    : ''}
                </div>
              </div>
              {w.score?.strain != null && (
                <div className="px-2.5 py-1 rounded-md bg-sky-50 border border-sky-200 text-sky-700 text-sm font-medium tabular-nums flex-shrink-0">
                  {w.score.strain.toFixed(1)}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </Section>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-stone-500 mb-3">{title}</div>
      {children}
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-stone-500">{label}</div>
      <div className="text-base tabular-nums mt-0.5">{value}</div>
    </div>
  )
}

function formatHm(ms: number): string {
  if (!ms || ms < 0) return '—'
  const totalMin = Math.round(ms / 60_000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

function prettyDate(iso: string): string {
  const d = new Date(iso + 'T12:00:00')
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

function timeOfDay(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

function capitalize(s: string): string {
  return s
    .split(/[\s_-]+/)
    .map((p) => (p[0]?.toUpperCase() ?? '') + p.slice(1))
    .join(' ')
}
